// src/pages/dealer/DealerMyOffers.jsx
// REPLACE THIS FILE — adds the 💳 Payment column

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealerAPI } from '../../services/dealerAPI';
import DealerPaymentModal from '../../components/dealer/DealerPaymentModal';
import './DealerMyOffers.css';

const fmt = (val) => val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

const STATUS_CONFIG = {
  pending:  { label: 'Pending',  bg: '#fef9c3', color: '#854d0e', icon: '🕐' },
  accepted: { label: 'Accepted', bg: '#dcfce7', color: '#166534', icon: '✅' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', icon: '❌' },
};

export default function DealerMyOffers() {
  const navigate = useNavigate();
  const [offers,       setOffers]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState('all');
  const [paymentOffer, setPaymentOffer] = useState(null);
  const [payStatuses,  setPayStatuses]  = useState({});

  useEffect(() => {
    dealerAPI.getMyOffers()
      .then(res => {
        const data = res.data?.data || [];
        setOffers(data);

        // fetch payment status only for accepted + winning offers
        const winners = data.filter(o => o.status === 'accepted' && o.is_highest);
        if (!winners.length) return;

        Promise.all(
          winners.map(o =>
            dealerAPI.getPaymentStatus(o.id)
              .then(r => ({ id: o.id, status: r.data?.status || 'not_initiated' }))
              .catch(() => ({ id: o.id, status: 'not_initiated' }))
          )
        ).then(results => {
          const map = {};
          results.forEach(r => { map[r.id] = r.status; });
          setPayStatuses(map);
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? offers : offers.filter(o => o.status === filter);

  const stats = {
    total:    offers.length,
    winning:  offers.filter(o => o.is_highest).length,
    accepted: offers.filter(o => o.status === 'accepted').length,
    pending:  offers.filter(o => o.status === 'pending').length,
  };

  /* ── Payment button per row ──────────────────────────────────────────── */
  const getPayBtn = (o) => {
    if (o.status !== 'accepted' || !o.is_highest) return null;

    const ps = payStatuses[o.id];

    if (ps === 'success') {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '5px 12px', borderRadius: 6,
          background: '#dcfce7', color: '#166534',
          border: '1px solid #86efac',
          fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        }}>
          ✅ Paid
        </span>
      );
    }

    return (
      <button
        onClick={e => { e.stopPropagation(); setPaymentOffer(o); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '8px 14px',
          background: ps === 'initiated'
            ? 'linear-gradient(135deg,#f59e0b,#d97706)'
            : 'linear-gradient(135deg,#FFD60A,#FFC107)',
          border: 'none', borderRadius: 8,
          color: '#003566', fontWeight: 800, fontSize: 12,
          cursor: 'pointer', whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(255,214,10,0.45)',
          transition: 'transform 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {ps === 'initiated' ? '⏳ Resume Payment' : '💳 Pay Now'}
      </button>
    );
  };

  return (
    <div className="dmo-root">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <div className="dmo-header">
        <h1 className="dmo-title">My Offers</h1>
        <p className="dmo-sub">Track all your submitted bids and their current status</p>
      </div>

      {/* Summary */}
      <div className="dmo-summary">
        {[
          { label: 'Total Offers',      val: stats.total,    color: '#003566' },
          { label: 'Currently Winning', val: stats.winning,  color: '#059669' },
          { label: 'Accepted',          val: stats.accepted, color: '#7c3aed' },
          { label: 'Pending',           val: stats.pending,  color: '#d97706' },
        ].map(s => (
          <div className="dmo-sum-card" key={s.label}>
            <span className="dmo-sum-val" style={{ color: s.color }}>{s.val}</span>
            <span className="dmo-sum-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="dmo-filters">
        {['all', 'pending', 'accepted', 'rejected'].map(f => (
          <button
            key={f}
            className={`dmo-filter ${filter === f ? 'dmo-filter--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{
              marginLeft: 6,
              background: filter === f ? 'rgba(255,214,10,0.25)' : '#f3f4f6',
              color: filter === f ? '#FFD60A' : '#9ca3af',
              fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
            }}>
              {f === 'all' ? offers.length : offers.filter(o => o.status === f).length}
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
          <div style={{ width: 32, height: 32, border: '4px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
          <span style={{ color: '#666', fontWeight: 600 }}>Loading your offers…</span>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="dmo-empty">
          <div className="dmo-empty-icon">🏷️</div>
          <p>No offers found in this category.</p>
          <button className="dmo-browse" onClick={() => navigate('/dealer/listings')}>
            Browse Listings
          </button>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="dmo-table-wrap">
          <table className="dmo-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Car</th>
                <th>Offer Price</th>
                <th>Status</th>
                <th>Position</th>
                <th>Date</th>
                <th>💳 Payment</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => {
                const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
                const isWinnerAccepted = o.status === 'accepted' && o.is_highest;

                return (
                  <tr
                    key={o.id}
                    className={o.is_highest ? 'dmo-tr--winning' : ''}
                    style={{
                      background: isWinnerAccepted
                        ? 'linear-gradient(90deg,rgba(255,214,10,0.07),white)'
                        : undefined,
                    }}
                  >
                    <td className="dmo-td-num">{i + 1}</td>

                    <td>
                      <div className="dmo-car-name">{o.car.brand} {o.car.model}</div>
                      <div className="dmo-listing-id">Listing #{o.listing_id}</div>
                    </td>

                    <td>
                      <span className="dmo-price">{fmt(o.offer_price)}</span>
                    </td>

                    <td>
                      <span className={`dmo-badge ${
                        o.status === 'accepted' ? 'dmo-badge--accepted' :
                        o.status === 'rejected' ? 'dmo-badge--rejected' :
                        'dmo-badge--pending'
                      }`}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </td>

                    <td>
                      {o.is_highest
                        ? <span className="dmo-winning-pill">🏆 Highest</span>
                        : <span className="dmo-outbid-pill">Outbid</span>
                      }
                    </td>

                    <td className="dmo-date">
                      {new Date(o.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </td>

                    {/* ── PAYMENT COLUMN ── */}
                    <td style={{ minWidth: 140 }}>
                      {getPayBtn(o)}
                    </td>

                    <td>
                      <button
                        className="dmo-view-btn"
                        onClick={() => navigate(`/dealer/listings/${o.listing_id}`)}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Modal */}
      {paymentOffer && (
        <DealerPaymentModal
          offer={paymentOffer}
          carLabel={`${paymentOffer.car.brand} ${paymentOffer.car.model}`}
          onClose={() => setPaymentOffer(null)}
          onSuccess={() => {
            setPayStatuses(prev => ({ ...prev, [paymentOffer.id]: 'success' }));
            setPaymentOffer(null);
          }}
        />
      )}
    </div>
  );
}